import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Play, Pause, SkipForward, Mic, Share2, MessageCircle, List, User, LogIn, LogOut, Lock } from 'lucide-react';
import { audioApi, getAudioFileUrl } from '@/lib/api';
import { AUDIO_COLORS, AUDIO_SHAPES, LANGUAGE_FLAGS } from '@/lib/constants';
import { AudioVisualBadge, ShapeSelector, ColorSelector, EmojiSelector, LanguageSelector } from '@/components/audio/AudioVisuals';
import { useAuth } from '@/contexts/AuthContext';

const RadioPlayer = () => {
  const { user, login, logout, isAuthenticated } = useAuth();
  const [audios, setAudios] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showRecorder, setShowRecorder] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const audioRef = useRef(null);

  const currentAudio = audios[currentIndex];
  const colorData = currentAudio ? AUDIO_COLORS[currentAudio.color] || AUDIO_COLORS.blue : null;

  // Load audios
  useEffect(() => {
    loadAudios();
  }, []);

  const loadAudios = async () => {
    try {
      const data = await audioApi.getFeed({ limit: 50 });
      setAudios(data);
      setLoading(false);
      // Auto-play first audio
      if (data.length > 0) {
        setTimeout(() => setIsPlaying(true), 500);
      }
    } catch (error) {
      console.error('Error loading audios:', error);
      setLoading(false);
    }
  };

  // Handle play/pause
  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play().catch(console.error);
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, currentIndex]);

  // Progress update
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const pct = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(pct || 0);
    }
  };

  // Auto-play next
  const handleEnded = useCallback(() => {
    if (currentIndex < audios.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setProgress(0);
      setTimeout(() => setIsPlaying(true), 1000);
    } else {
      setCurrentIndex(0);
      setProgress(0);
    }
  }, [currentIndex, audios.length]);

  // Skip to next
  const handleNext = () => {
    if (currentIndex < audios.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setProgress(0);
    }
  };

  // Reply (Réplica)
  const handleReply = () => {
    if (!isAuthenticated) {
      login();
      return;
    }
    // Check if replies are allowed
    if (currentAudio.allow_replies === false) {
      alert('El autor ha desactivado las réplicas para este audio');
      return;
    }
    setReplyTo(currentAudio);
    setShowRecorder(true);
  };

  // Share (Eco) - Opens share menu with WhatsApp, etc.
  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/audio/${currentAudio.audio_id}`;
    const shareText = `🎙️ Escucha esto en AudioToo`;
    
    try {
      await audioApi.shareAudio(currentAudio.audio_id);
      
      // Try native share first (mobile)
      if (navigator.share) {
        await navigator.share({ 
          title: 'AudioToo', 
          text: shareText, 
          url: shareUrl 
        });
      } else {
        // Desktop fallback - copy and show WhatsApp link
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
        window.open(whatsappUrl, '_blank');
      }
    } catch (err) {
      // User cancelled or error - copy to clipboard
      await navigator.clipboard.writeText(shareUrl);
      alert('✅ Enlace copiado');
    }
  };

  // View thread (Hilo)
  const handleThread = () => {
    window.location.href = `/hilo/${currentAudio.thread_id || currentAudio.audio_id}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-500">Cargando audios...</p>
        </div>
      </div>
    );
  }

  if (audios.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-6xl mb-4">🎙️</p>
          <h2 className="text-xl font-bold text-gray-800 mb-2">No hay audios aún</h2>
          <p className="text-gray-500 mb-4">Sé el primero en grabar</p>
          {isAuthenticated ? (
            <button 
              onClick={() => setShowRecorder(true)}
              className="bg-indigo-500 text-white px-6 py-3 rounded-full font-medium"
            >
              Grabar audio
            </button>
          ) : (
            <button 
              onClick={login}
              className="bg-indigo-500 text-white px-6 py-3 rounded-full font-medium"
            >
              Iniciar sesión
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: colorData ? `${colorData.hex}10` : '#f9fafb' }}
    >
      {/* Header */}
      <header className="flex items-center justify-between p-3 bg-white/90 backdrop-blur border-b">
        <Link to="/" className="flex items-center gap-2">
          <span className="text-2xl">🎙️</span>
          <span className="font-bold text-gray-800">AudioToo</span>
        </Link>
        <div className="flex items-center gap-2">
          {isAuthenticated ? (
            <>
              <button 
                onClick={() => setShowRecorder(true)}
                className="bg-red-500 text-white p-2.5 rounded-full"
                title="Grabar audio"
              >
                <Mic size={20} />
              </button>
              <button onClick={logout} className="p-2 text-gray-400" title="Salir">
                <LogOut size={20} />
              </button>
            </>
          ) : (
            <button 
              onClick={login}
              className="bg-indigo-500 text-white px-4 py-2 rounded-full text-sm flex items-center gap-1"
            >
              <LogIn size={16} />
              Entrar
            </button>
          )}
        </div>
      </header>

      {/* Main Player */}
      <main className="flex-1 flex flex-col items-center justify-center p-4">
        {/* Visual Badge - Shape + Color + Emoji + Flag */}
        <div className="mb-4">
          <AudioVisualBadge
            shape={currentAudio.shape || 'circle'}
            color={currentAudio.color || 'blue'}
            emoji={currentAudio.emoji || '🎙️'}
            language={currentAudio.language || 'ES'}
            size="lg"
          />
        </div>

        {/* Shape & Color labels */}
        <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
          <span className="px-2 py-0.5 rounded-full" style={{ backgroundColor: `${colorData?.hex}20` }}>
            {AUDIO_SHAPES[currentAudio.shape]?.name || 'Audio'}
          </span>
          <span>•</span>
          <span>{colorData?.name || 'General'}</span>
        </div>

        {/* User Info */}
        <div className="flex items-center gap-2 mb-1">
          {currentAudio.user_picture ? (
            <img src={currentAudio.user_picture} alt="" className="w-6 h-6 rounded-full" />
          ) : (
            <User size={18} className="text-gray-400" />
          )}
          <span className="font-medium text-gray-800">{LANGUAGE_FLAGS[currentAudio.language]?.flag} @{currentAudio.username}</span>
          {currentAudio.allow_replies === false && (
            <Lock size={14} className="text-gray-400" title="Réplicas desactivadas" />
          )}
        </div>
        
        <p className="text-xs text-gray-400 mb-4">
          {currentIndex + 1} de {audios.length}
        </p>

        {/* Progress Bar */}
        <div className="w-full max-w-xs h-1.5 bg-gray-200 rounded-full mb-6 overflow-hidden">
          <div 
            className="h-full rounded-full transition-all"
            style={{ width: `${progress}%`, backgroundColor: colorData?.hex || '#6366f1' }}
          />
        </div>

        {/* Play Controls */}
        <div className="flex items-center gap-6 mb-6">
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="w-16 h-16 rounded-full bg-white shadow-lg flex items-center justify-center transition-transform active:scale-95"
            style={{ color: colorData?.hex || '#6366f1' }}
          >
            {isPlaying ? <Pause size={32} /> : <Play size={32} className="ml-1" />}
          </button>
          <button
            onClick={handleNext}
            className="w-12 h-12 rounded-full bg-white shadow flex items-center justify-center text-gray-500 transition-transform active:scale-95"
          >
            <SkipForward size={24} />
          </button>
        </div>

        {/* Audio Element */}
        <audio
          ref={audioRef}
          src={currentAudio ? getAudioFileUrl(currentAudio.audio_url) : ''}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleEnded}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        />
      </main>

      {/* Action Buttons */}
      <footer className="bg-white border-t pt-3 pb-16">
        <div className="flex justify-around max-w-sm mx-auto">
          {/* Réplica */}
          <button
            onClick={handleReply}
            className={`flex flex-col items-center gap-0.5 ${currentAudio.allow_replies === false ? 'opacity-50' : ''}`}
          >
            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center relative">
              <MessageCircle size={24} className="text-indigo-600" />
              {currentAudio.allow_replies === false && (
                <Lock size={12} className="absolute -top-1 -right-1 text-gray-400" />
              )}
            </div>
            <span className="text-xs text-gray-600">Réplica</span>
          </button>

          {/* Eco (Share) */}
          <button
            onClick={handleShare}
            className="flex flex-col items-center gap-0.5"
          >
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <Share2 size={24} className="text-green-600" />
            </div>
            <span className="text-xs text-gray-600">Eco</span>
          </button>

          {/* Hilo */}
          <button
            onClick={handleThread}
            className="flex flex-col items-center gap-0.5"
          >
            <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
              <List size={24} className="text-orange-600" />
            </div>
            <span className="text-xs text-gray-600">Hilo</span>
          </button>
        </div>
      </footer>

      {/* Recorder Modal */}
      {showRecorder && (
        <AudioRecorderModal 
          replyTo={replyTo}
          onClose={() => { setShowRecorder(false); setReplyTo(null); }}
          onSuccess={() => { setShowRecorder(false); setReplyTo(null); loadAudios(); }}
        />
      )}
    </div>
  );
};

// GRABADOR COMPLETO con forma, color, emoji
const AudioRecorderModal = ({ replyTo, onClose, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: record, 2: classify
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState('');
  
  // Classification
  const [shape, setShape] = useState('circle');
  const [color, setColor] = useState('blue');
  const [emoji, setEmoji] = useState('🎙️');
  const [language, setLanguage] = useState('ES');
  const [allowReplies, setAllowReplies] = useState(true);
  
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      setError('');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => chunksRef.current.push(e.data);
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setRecordedBlob(blob);
        setRecordedUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setDuration(0);

      timerRef.current = setInterval(() => {
        setDuration(prev => {
          if (prev >= 30) {
            stopRecording();
            return 30;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      setError('No se pudo acceder al micrófono');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const handleUpload = async () => {
    if (!recordedBlob || duration < 3) {
      setError('Graba al menos 3 segundos');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', recordedBlob, `audio_${Date.now()}.webm`);
      formData.append('shape', shape);
      formData.append('color', color);
      formData.append('emoji', emoji);
      formData.append('language', language);
      formData.append('duration_seconds', duration.toString());
      formData.append('allow_replies', allowReplies.toString());
      
      if (replyTo) {
        formData.append('reply_to', replyTo.audio_id);
        formData.append('thread_id', replyTo.thread_id || replyTo.audio_id);
      }

      await audioApi.uploadAudio(formData);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al publicar');
    } finally {
      setUploading(false);
    }
  };

  const colorData = AUDIO_COLORS[color];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-3xl p-5 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">
            {replyTo ? `Responder a @${replyTo.username}` : 'Grabar audio'}
          </h3>
          <button onClick={onClose} className="text-gray-400 text-2xl leading-none">&times;</button>
        </div>

        {error && <p className="text-red-500 text-sm mb-3 bg-red-50 p-2 rounded">{error}</p>}

        {/* Step 1: Record */}
        {step === 1 && (
          <div className="text-center">
            {!recordedBlob ? (
              <>
                <button
                  onClick={isRecording ? stopRecording : startRecording}
                  className={`w-24 h-24 rounded-full flex items-center justify-center text-white mx-auto ${
                    isRecording ? 'bg-red-500 animate-pulse' : 'bg-red-500'
                  }`}
                >
                  <Mic size={40} />
                </button>
                <p className="mt-3 text-3xl font-mono">{duration}s</p>
                <p className="text-sm text-gray-500">máximo 30 segundos</p>
                <div className="w-48 h-1.5 bg-gray-200 rounded-full mt-3 mx-auto">
                  <div 
                    className="h-full bg-red-500 rounded-full transition-all" 
                    style={{ width: `${(duration/30)*100}%` }} 
                  />
                </div>
              </>
            ) : (
              <>
                <audio src={recordedUrl} controls className="w-full mb-4" />
                <p className="text-sm text-gray-500 mb-4">Duración: {duration}s</p>
                <div className="flex gap-3 justify-center">
                  <button 
                    onClick={() => { setRecordedBlob(null); setRecordedUrl(''); setDuration(0); }}
                    className="px-4 py-2 bg-gray-100 rounded-full text-sm"
                  >
                    Grabar de nuevo
                  </button>
                  <button 
                    onClick={() => setStep(2)}
                    className="px-6 py-2 bg-indigo-500 text-white rounded-full text-sm"
                  >
                    Continuar
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Step 2: Classify */}
        {step === 2 && (
          <div className="space-y-4">
            {/* Preview */}
            <div className="flex justify-center mb-2">
              <AudioVisualBadge shape={shape} color={color} emoji={emoji} language={language} size="md" />
            </div>

            {/* Shape */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Tipo de audio</p>
              <ShapeSelector selected={shape} onSelect={setShape} />
            </div>

            {/* Color */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Temática</p>
              <ColorSelector selected={color} onSelect={setColor} />
            </div>

            {/* Emoji */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Emoji</p>
              <EmojiSelector selected={emoji} onSelect={setEmoji} />
            </div>

            {/* Language */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Idioma</p>
              <LanguageSelector selected={language} onSelect={setLanguage} />
            </div>

            {/* Allow Replies Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-700">Permitir réplicas</p>
                <p className="text-xs text-gray-500">Otros pueden responder a este audio</p>
              </div>
              <button
                onClick={() => setAllowReplies(!allowReplies)}
                className={`w-12 h-6 rounded-full transition-colors ${
                  allowReplies ? 'bg-green-500' : 'bg-gray-300'
                }`}
              >
                <div 
                  className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    allowReplies ? 'translate-x-6' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button 
                onClick={() => setStep(1)}
                className="flex-1 py-3 bg-gray-100 rounded-full text-sm"
              >
                Atrás
              </button>
              <button 
                onClick={handleUpload}
                disabled={uploading}
                className="flex-1 py-3 text-white rounded-full text-sm font-medium disabled:opacity-50"
                style={{ backgroundColor: colorData?.hex || '#6366f1' }}
              >
                {uploading ? 'Publicando...' : 'Publicar'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RadioPlayer;
